import React from 'react';
import * as THREE from 'three';
import { useAppStore, Layer } from '../store/useAppStore';
import { buildLayerTransform, buildEffectiveLayerTransform } from '../utils/viewportMathUtils';
import {
  projectBackHitToNdc,
  worldPointToUV,
  uvAlignedTangentFrame,
  camFrustumHalfH,
  getDuplicationCenters,
  wrapStampDiam,
} from '../utils/viewportRenderUtils';

/** Permanent identity Matrix3 for text-preview pass where no rotation is applied. */
const _identityMatrix3 = new THREE.Matrix3();

export interface StampPipelineCtx {
  glRef: React.MutableRefObject<any>;
  cameraRef: React.MutableRefObject<THREE.Camera | null>;
  orthoCamera: React.MutableRefObject<THREE.OrthographicCamera | null>;
  stampMaterial: React.MutableRefObject<THREE.ShaderMaterial | null>;
  stampScene: React.MutableRefObject<THREE.Scene | null>;
  stampTexture: React.MutableRefObject<THREE.CanvasTexture | null>;
  strokeRenderTargetA: React.MutableRefObject<THREE.WebGLRenderTarget | null>;
  strokeRenderTargetB: React.MutableRefObject<THREE.WebGLRenderTarget | null>;
  transparentBaseRT: React.MutableRefObject<THREE.WebGLRenderTarget | null>;
  strokeMaterialRef: React.MutableRefObject<THREE.MeshBasicMaterial | null>;
  invalidateRef: React.MutableRefObject<() => void>;
  stampSizeRef: React.MutableRefObject<number>;
  stampProjectionModeRef: React.MutableRefObject<'wrap' | 'project' | 'inverse'>;
  stampIsPresetRef: React.MutableRefObject<boolean>;
  stampRotationRef: React.MutableRefObject<number>;
  stampFlipXRef: React.MutableRefObject<boolean>;
  stampFlipYRef: React.MutableRefObject<boolean>;
  brushColorRef: React.MutableRefObject<any>;
  ctrlHeldRef: React.MutableRefObject<boolean>;
  pickerValueRef: React.MutableRefObject<any>;
  brushOpacityRef: React.MutableRefObject<number>;
  textStampTextureRef: React.MutableRefObject<THREE.CanvasTexture | null>;
  textContentRef: React.MutableRefObject<string>;
  textFontRef: React.MutableRefObject<string>;
  textFontSizeRef: React.MutableRefObject<number>;
  textBoldRef: React.MutableRefObject<boolean>;
  textItalicRef: React.MutableRefObject<boolean>;
  textJustifyRef: React.MutableRefObject<'left' | 'center' | 'right'>;
  textLetterSpacingRef: React.MutableRefObject<number>;
  textLineHeightRef: React.MutableRefObject<number>;
  textCanvasCacheRef: React.MutableRefObject<HTMLCanvasElement | null>;
  textCanvasCacheKeyRef: React.MutableRefObject<string>;
  lastStampHitPoint: React.MutableRefObject<THREE.Vector3 | null>;
  selectedLayerIdRef: React.MutableRefObject<string | null>;
  layerRTs: React.MutableRefObject<Map<string, { a: THREE.WebGLRenderTarget; b: THREE.WebGLRenderTarget; active: 'a' | 'b' }>>;
  layersRef: React.MutableRefObject<Layer[]>;
  dupModeRef: React.MutableRefObject<'hero' | 'diptych' | 'triptych' | 'quadriptych'>;
  isDirtyRef: React.MutableRefObject<boolean>;
  autosaveNotifyDirtyRef: React.MutableRefObject<() => void>;
  flattenLayersRef: React.MutableRefObject<() => void>;
  scheduleBumpRef: React.MutableRefObject<(after?: () => void) => void>;
  generateLayerThumbnailRef: React.MutableRefObject<(id: string) => void>;
  markLayersDirtyRef: React.MutableRefObject<(...ids: string[]) => void>;
}

export function useStampPipeline(ctx: StampPipelineCtx) {

  /** Renders the current text settings to a canvas using the brush color.
   *  Two-pass composite approach to get clean antialiased glyphs. */
  const buildTextCanvas = (): HTMLCanvasElement => {
    const rawContent = ctx.textContentRef.current || ' ';
    const lines = rawContent.split('\n').map((l) => l || ' ');
    const BASE_STAMP_SIZE = 0.08;
    const stampScale = Math.max(1, ctx.stampSizeRef.current / BASE_STAMP_SIZE);
    const fontSize = Math.min(2048, Math.round(ctx.textFontSizeRef.current * stampScale));
    const fontStr = `${ctx.textItalicRef.current ? 'italic ' : ''}${ctx.textBoldRef.current ? 'bold ' : ''}${fontSize}px "${ctx.textFontRef.current}"`;
    const color = ctx.brushColorRef.current;
    const justify = ctx.textJustifyRef.current;
    // letterSpacing is stored as an em fraction; convert to px for Canvas API.
    const letterSpacingPx = Math.round(ctx.textLetterSpacingRef.current * fontSize);
    const letterSpacingStr = `${letterSpacingPx}px`;

    // Return the cached canvas when nothing has changed — avoids expensive
    // rasterization on every pointermove when only the hit point moves.
    const cacheKey = `${rawContent}|${fontStr}|${color}|${justify}|${letterSpacingStr}|${ctx.textLineHeightRef.current}`;
    if (ctx.textCanvasCacheKeyRef.current === cacheKey && ctx.textCanvasCacheRef.current) {
      return ctx.textCanvasCacheRef.current;
    }

    // Measure each line on a scratch canvas to determine overall canvas size.
    const tmp = document.createElement('canvas');
    tmp.width = 4; tmp.height = 4;
    const tmpCtx = tmp.getContext('2d')!;
    tmpCtx.font = fontStr;
    (tmpCtx as any).letterSpacing = letterSpacingStr;
    const lineWidths = lines.map((l) => tmpCtx.measureText(l).width);
    const maxLineWidth = Math.max(...lineWidths);
    const padding = Math.max(4, Math.round(fontSize * 0.08));
    const lineHeight = Math.ceil(fontSize * ctx.textLineHeightRef.current);
    const w = Math.max(1, Math.ceil(maxLineWidth) + padding * 2);
    const h = Math.max(1, lineHeight * lines.length);

    // Horizontal draw position for each justification mode.
    let textX: number;
    let textAlign: CanvasTextAlign;
    if (justify === 'center') { textX = w / 2; textAlign = 'center'; }
    else if (justify === 'right') { textX = w - padding; textAlign = 'right'; }
    else { textX = padding; textAlign = 'left'; }

    // Pass 1: glyph alpha mask (render in white; RGB values are irrelevant).
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = w; maskCanvas.height = h;
    const maskCtx = maskCanvas.getContext('2d')!;
    maskCtx.font = fontStr;
    (maskCtx as any).letterSpacing = letterSpacingStr;
    maskCtx.textAlign = textAlign;
    maskCtx.textBaseline = 'middle';
    maskCtx.fillStyle = 'white';
    lines.forEach((line, i) => {
      const y = lineHeight * i + lineHeight / 2;
      maskCtx.fillText(line, textX, y);
    });

    // Pass 2: solid color fill clipped to the glyph alpha mask.
    const c = document.createElement('canvas');
    c.width = w; c.height = h;
    const ctx2d = c.getContext('2d')!;
    ctx2d.fillStyle = color;
    ctx2d.fillRect(0, 0, w, h);
    ctx2d.globalCompositeOperation = 'destination-in';
    ctx2d.drawImage(maskCanvas, 0, 0);
    ctx2d.globalCompositeOperation = 'source-over';

    // Invalidate cached GPU texture so it gets recreated at the new dimensions.
    if (ctx.textStampTextureRef.current) {
      ctx.textStampTextureRef.current.dispose();
      ctx.textStampTextureRef.current = null;
    }
    ctx.textCanvasCacheKeyRef.current = cacheKey;
    ctx.textCanvasCacheRef.current = c;
    return c;
  };

  /** Shared helper that sets all stamp-shader uniforms from an arbitrary canvas. */
  const setStampUniforms = (
    canvas: HTMLCanvasElement,
    texRef: React.MutableRefObject<THREE.CanvasTexture | null>,
    hitPoint: THREE.Vector3,
    baseRT: THREE.WebGLRenderTarget,
    opacity: number,
    rotFwd: THREE.Matrix3,
    stampCenter?: THREE.Vector3,
    dupQuaternion?: THREE.Quaternion,
  ) => {
    const cam = ctx.cameraRef.current!;
    const img = canvas;

    if (!texRef.current) {
      texRef.current = new THREE.CanvasTexture(img);
      // Canvas 2D context writes sRGB data; mark accordingly so the GPU
      // decodes sRGB→linear when sampling (matching the fill/draw pipeline).
      texRef.current.colorSpace = THREE.SRGBColorSpace;
    } else {
      texRef.current.image = img;
      texRef.current.needsUpdate = true;
    }

    const center = stampCenter ?? hitPoint.clone().normalize();
    const baseScale = ctx.stampSizeRef.current * cam.position.length();
    const isProjMode = ctx.stampProjectionModeRef.current === 'project';
    const isInverseMode = ctx.stampProjectionModeRef.current === 'inverse';
    const isProjLikeMode = isProjMode || isInverseMode;
    const q = dupQuaternion ?? new THREE.Quaternion();

    let viewProj = new THREE.Matrix4();
    let centerNDC = new THREE.Vector2();
    let halfSizeNDC = new THREE.Vector2();
    let camPosForUniform: THREE.Vector3 = cam.position;
    if (isProjLikeMode) {
      const rotMatInv = new THREE.Matrix4().makeRotationFromQuaternion(q.clone().invert());
      const viewMatRotated = cam.matrixWorldInverse.clone().multiply(rotMatInv);
      viewProj = new THREE.Matrix4().multiplyMatrices(cam.projectionMatrix, viewMatRotated);
      camPosForUniform = cam.position.clone().applyQuaternion(q);
      if (isInverseMode) {
        if (!projectBackHitToNdc(center, camPosForUniform, viewProj, centerNDC)) {
          const ndc = hitPoint.clone().project(cam);
          centerNDC.set(ndc.x, ndc.y);
        }
      } else {
        // NDC center: project original hit through original camera — same result
        // as projecting the rotated hit through the rotated camera.
        const ndc = hitPoint.clone().project(cam);
        centerNDC.set(ndc.x, ndc.y);
      }
      const viewAspect = (ctx.glRef.current?.domElement.clientWidth ?? 1) /
                         (ctx.glRef.current?.domElement.clientHeight ?? 1);
      const imgAspect = img.width / img.height;
      const camDist1 = cam.position.length();
      const halfY = (ctx.stampSizeRef.current * camDist1) / camFrustumHalfH(cam, camDist1);
      halfSizeNDC.set(halfY * imgAspect / viewAspect, halfY);
    }

    // Build tangent frame at the ORIGINAL hit point, then rotate it by the
    // dup quaternion so copies are true rotational duplicates.
    const origCenter = hitPoint.clone().normalize();
    let baseRight: THREE.Vector3;
    let baseUp: THREE.Vector3;
    if (useAppStore.getState().uvViewportMain) {
      // UV panel is primary: align with equirectangular grid.
      ({ sRight: baseRight, sUp: baseUp } = uvAlignedTangentFrame(origCenter));
    } else {
      const camRight = new THREE.Vector3().setFromMatrixColumn(cam.matrixWorld as THREE.Matrix4, 0).normalize();
      const camUp    = new THREE.Vector3().setFromMatrixColumn(cam.matrixWorld as THREE.Matrix4, 1).normalize();
      baseRight = camRight.clone().addScaledVector(origCenter, -camRight.dot(origCenter)).normalize();
      baseUp    = camUp.clone().addScaledVector(origCenter, -camUp.dot(origCenter)).normalize();
    }
    const sRight = baseRight.clone().applyQuaternion(q);
    const sUp    = baseUp.clone().applyQuaternion(q);
    const aspect = img.width / img.height;

    const m = ctx.stampMaterial.current!.uniforms;
    m.uBase.value          = baseRT.texture;
    m.uStamp.value         = texRef.current;
    m.uStampCenter.value   = center;
    m.uStampRight.value    = sRight;
    m.uStampUp.value       = sUp;
    m.uStampCenterUV.value = worldPointToUV(center);
    m.uStampScale.value    = isProjLikeMode
      ? new THREE.Vector2(baseScale, baseScale / aspect)
      : new THREE.Vector2(wrapStampDiam(cam.position.length(), ctx.stampSizeRef.current) * aspect, wrapStampDiam(cam.position.length(), ctx.stampSizeRef.current));
    m.uStampOpacity.value  = opacity;
    // Text canvases have the brush color baked in already; reset to white so
    // the shader's uStampColor tint does not double-apply the color.
    m.uStampColor.value.setRGB(1, 1, 1);
    m.uStampFlipX.value = ctx.stampFlipXRef.current ? 1 : 0;
    m.uStampFlipY.value = ctx.stampFlipYRef.current ? 1 : 0;
    m.uProjMode.value      = isProjMode ? 1 : isInverseMode ? 2 : 0;
    m.uCamViewProj.value   = viewProj;
    m.uStampCenterNDC.value    = centerNDC;
    m.uStampHalfSizeNDC.value  = halfSizeNDC;
    m.uCamPos.value        = camPosForUniform;
    m.uStampRotation.value = ctx.stampRotationRef.current * Math.PI / 180;
    m.uStampAspect.value   = aspect;
    m.uRotFwd.value        = rotFwd;
  };

  // ── Stamp ghost preview ─────────────────────────────────────────────────────
  const renderStampPreview = (hitPoint: THREE.Vector3) => {
    const gl = ctx.glRef.current;
    const img = useAppStore.getState().stampImage;
    const rtA = ctx.strokeRenderTargetA.current;
    const rtB = ctx.strokeRenderTargetB.current;
    if (!gl || !img || !ctx.stampMaterial.current || !ctx.stampScene.current ||
        !ctx.orthoCamera.current || !ctx.transparentBaseRT.current || !rtA || !rtB) return;

    ctx.lastStampHitPoint.current = hitPoint;

    // Create/update stamp texture.
    if (!ctx.stampTexture.current) {
      ctx.stampTexture.current = new THREE.CanvasTexture(img);
      ctx.stampTexture.current.colorSpace = THREE.SRGBColorSpace;
    } else {
      ctx.stampTexture.current.image = img;
      ctx.stampTexture.current.needsUpdate = true;
    }

    const cam = ctx.cameraRef.current!;
    const baseScale = ctx.stampSizeRef.current * cam.position.length();
    const isProjMode = ctx.stampProjectionModeRef.current === 'project';
    const isInverseMode = ctx.stampProjectionModeRef.current === 'inverse';
    const isProjLikeMode = isProjMode || isInverseMode;
    const aspect = img.width / img.height;

    // Projection-mode uniforms computed once from the primary hit point.
    let viewProjBase = new THREE.Matrix4();
    let centerNDCBase = new THREE.Vector2();
    let halfSizeNDCBase = new THREE.Vector2();
    if (isProjLikeMode) {
      viewProjBase = new THREE.Matrix4().multiplyMatrices(
        cam.projectionMatrix, cam.matrixWorldInverse
      );
      if (isInverseMode) {
        if (!projectBackHitToNdc(hitPoint, cam.position, viewProjBase, centerNDCBase)) {
          const ndc = hitPoint.clone().project(cam);
          centerNDCBase.set(ndc.x, ndc.y);
        }
      } else {
        const ndc = hitPoint.clone().project(cam);
        centerNDCBase.set(ndc.x, ndc.y);
      }
      const viewAspect = (ctx.glRef.current?.domElement.clientWidth ?? 1) /
                         (ctx.glRef.current?.domElement.clientHeight ?? 1);
      const camDist = cam.position.length();
      const halfY = (ctx.stampSizeRef.current * camDist) / camFrustumHalfH(cam, camDist);
      halfSizeNDCBase.set(halfY * aspect / viewAspect, halfY);
    }

    // Base tangent frame at the original hit point.
    const origCenter = hitPoint.clone().normalize();
    let origRight: THREE.Vector3;
    let origUp: THREE.Vector3;
    if (useAppStore.getState().uvViewportMain) {
      ({ sRight: origRight, sUp: origUp } = uvAlignedTangentFrame(origCenter));
    } else {
      const camRight = new THREE.Vector3().setFromMatrixColumn(cam.matrixWorld as THREE.Matrix4, 0).normalize();
      const camUp    = new THREE.Vector3().setFromMatrixColumn(cam.matrixWorld as THREE.Matrix4, 1).normalize();
      origRight = camRight.clone().addScaledVector(origCenter, -camRight.dot(origCenter)).normalize();
      origUp    = camUp.clone().addScaledVector(origCenter, -camUp.dot(origCenter)).normalize();
    }

    // Stamp color (same logic as committed apply).
    let previewStampColor: string = Array.isArray(ctx.brushColorRef.current)
      ? (ctx.brushColorRef.current as any)[0].color
      : ctx.brushColorRef.current;
    if (ctx.ctrlHeldRef.current) {
      const pv = ctx.pickerValueRef.current;
      if (pv && typeof pv.getColors === 'function') {
        const stops: Array<{ color: any; percent: number }> = pv.getColors();
        if (stops && stops.length > 0) {
          const last = stops[stops.length - 1];
          if (last.color && typeof last.color.toHexString === 'function') {
            const hex8 = last.color.toHexString();
            previewStampColor = hex8.length >= 9 ? hex8.slice(0, 7) : hex8;
          }
        }
      }
    }

    // Ping-pong across all dup copies so every mirrored position is ghosted.
    const copies = getDuplicationCenters(origCenter, ctx.dupModeRef.current);
    let srcRT: THREE.WebGLRenderTarget = ctx.transparentBaseRT.current;
    let dstRT: THREE.WebGLRenderTarget = rtA;
    let toggleA = true;

    for (const { center, quaternion: q } of copies) {
      gl.setRenderTarget(dstRT);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.setRenderTarget(null);

      const sRight = origRight.clone().applyQuaternion(q);
      const sUp    = origUp.clone().applyQuaternion(q);

      let viewProj = viewProjBase;
      let camPosForUniform = cam.position;
      if (isProjLikeMode) {
        const rotMatInv = new THREE.Matrix4().makeRotationFromQuaternion(q.clone().invert());
        const viewMatRotated = cam.matrixWorldInverse.clone().multiply(rotMatInv);
        viewProj = new THREE.Matrix4().multiplyMatrices(cam.projectionMatrix, viewMatRotated);
        camPosForUniform = cam.position.clone().applyQuaternion(q);
      }

      const m = ctx.stampMaterial.current!.uniforms;
      m.uBase.value             = srcRT.texture;
      m.uStamp.value            = ctx.stampTexture.current;
      m.uStampCenter.value      = center;
      m.uStampRight.value       = sRight;
      m.uStampUp.value          = sUp;
      m.uStampCenterUV.value    = worldPointToUV(center);
      m.uStampScale.value       = isProjLikeMode
        ? new THREE.Vector2(baseScale, baseScale / aspect)
        : new THREE.Vector2(wrapStampDiam(cam.position.length(), ctx.stampSizeRef.current) * aspect, wrapStampDiam(cam.position.length(), ctx.stampSizeRef.current));
      m.uStampOpacity.value     = 1.0;
      m.uStampColor.value.setStyle(ctx.stampIsPresetRef.current ? previewStampColor : '#ffffff');
      m.uProjMode.value         = isProjMode ? 1 : isInverseMode ? 2 : 0;
      m.uCamViewProj.value      = viewProj;
      m.uStampCenterNDC.value   = centerNDCBase;
      m.uStampHalfSizeNDC.value = halfSizeNDCBase;
      m.uCamPos.value           = camPosForUniform;
      m.uStampRotation.value    = ctx.stampRotationRef.current * Math.PI / 180;
      m.uStampAspect.value      = aspect;
      m.uStampFlipX.value       = ctx.stampFlipXRef.current ? 1 : 0;
      m.uStampFlipY.value       = ctx.stampFlipYRef.current ? 1 : 0;
      m.uRotFwd.value           = new THREE.Matrix3(); // identity for preview

      gl.setRenderTarget(dstRT);
      gl.render(ctx.stampScene.current, ctx.orthoCamera.current);
      gl.setRenderTarget(null);

      srcRT = dstRT;
      dstRT = toggleA ? rtB : rtA;
      toggleA = !toggleA;
    }

    // srcRT holds the fully composited preview (all dup positions).
    if (ctx.strokeMaterialRef.current) {
      ctx.strokeMaterialRef.current.blendSrc = THREE.SrcAlphaFactor;
      ctx.strokeMaterialRef.current.map = srcRT.texture;
      ctx.strokeMaterialRef.current.opacity = 0.6;
      ctx.strokeMaterialRef.current.needsUpdate = true;
    }
    ctx.invalidateRef.current();
  };

  const clearStampPreview = () => {
    const gl = ctx.glRef.current;
    const rtA = ctx.strokeRenderTargetA.current;
    const rtB = ctx.strokeRenderTargetB.current;
    if (!gl || !rtA) return;

    gl.setRenderTarget(rtA);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    if (rtB) {
      gl.setRenderTarget(rtB);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
    }
    gl.setRenderTarget(null);

    if (ctx.strokeMaterialRef.current) {
      ctx.strokeMaterialRef.current.opacity = 1.0;
      ctx.strokeMaterialRef.current.needsUpdate = true;
    }
    ctx.invalidateRef.current();
  };

  // ── Text tool helpers ────────────────────────────────────────────────────────

  const renderTextPreview = (hitPoint: THREE.Vector3) => {
    const gl = ctx.glRef.current;
    const rtA = ctx.strokeRenderTargetA.current;
    const rtB = ctx.strokeRenderTargetB.current;
    if (!gl || !ctx.stampMaterial.current || !ctx.stampScene.current ||
        !ctx.orthoCamera.current || !ctx.transparentBaseRT.current || !rtA || !rtB) return;

    ctx.lastStampHitPoint.current = hitPoint;
    const canvas = buildTextCanvas();

    // Ping-pong across all dup copies.
    const copies = getDuplicationCenters(hitPoint.clone().normalize(), ctx.dupModeRef.current);
    let srcRT: THREE.WebGLRenderTarget = ctx.transparentBaseRT.current;
    let dstRT: THREE.WebGLRenderTarget = rtA;
    let toggleA = true;

    for (const { center, quaternion } of copies) {
      gl.setRenderTarget(dstRT);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.setRenderTarget(null);

      setStampUniforms(canvas, ctx.textStampTextureRef, hitPoint,
        srcRT, 1.0, _identityMatrix3, center, quaternion);

      gl.setRenderTarget(dstRT);
      gl.render(ctx.stampScene.current, ctx.orthoCamera.current);
      gl.setRenderTarget(null);

      srcRT = dstRT;
      dstRT = toggleA ? rtB : rtA;
      toggleA = !toggleA;
    }

    if (ctx.strokeMaterialRef.current) {
      ctx.strokeMaterialRef.current.blendSrc = THREE.SrcAlphaFactor;
      ctx.strokeMaterialRef.current.map = srcRT.texture;
      ctx.strokeMaterialRef.current.opacity = 0.6;
      ctx.strokeMaterialRef.current.needsUpdate = true;
    }
    ctx.invalidateRef.current();
  };

  // ── Stamp application ───────────────────────────────────────────────────────

  const applyTextStamp = (hitPoint: THREE.Vector3) => {
    const gl = ctx.glRef.current;
    const selId = ctx.selectedLayerIdRef.current;
    if (!gl || !selId || !ctx.stampMaterial.current || !ctx.stampScene.current || !ctx.orthoCamera.current) return;

    const lRTData = ctx.layerRTs.current.get(selId);
    if (!lRTData) return;

    const canvas = buildTextCanvas();

    const layer = ctx.layersRef.current.find((l) => l.id === selId);
    const rotFwd = layer
      ? new THREE.Matrix3().setFromMatrix4(buildEffectiveLayerTransform(layer, ctx.layersRef.current))
      : new THREE.Matrix3();

    const baseCenter = hitPoint.clone().normalize();
    const copies = getDuplicationCenters(baseCenter, ctx.dupModeRef.current);

    for (const { center, quaternion } of copies) {
      const srcRT = lRTData.active === 'a' ? lRTData.a : lRTData.b;
      const dstRT = lRTData.active === 'a' ? lRTData.b : lRTData.a;

      setStampUniforms(canvas, ctx.textStampTextureRef, hitPoint,
        srcRT, ctx.brushOpacityRef.current, rotFwd, center, quaternion);

      gl.setRenderTarget(dstRT);
      gl.render(ctx.stampScene.current, ctx.orthoCamera.current);
      gl.setRenderTarget(null);

      lRTData.active = lRTData.active === 'a' ? 'b' : 'a';
    }

    ctx.isDirtyRef.current = true;
    ctx.autosaveNotifyDirtyRef.current();
    if (selId) useAppStore.getState().updateLayer(selId, { lastDupMode: ctx.dupModeRef.current });
    if (selId) ctx.markLayersDirtyRef.current(selId);
    ctx.flattenLayersRef.current();
    ctx.scheduleBumpRef.current(() => {
      if (selId) ctx.generateLayerThumbnailRef.current(selId);
    });
  };

  const applyStamp = (hitPoint: THREE.Vector3) => {
    const gl = ctx.glRef.current;
    const selId = ctx.selectedLayerIdRef.current;
    const img = useAppStore.getState().stampImage;
    if (!gl || !selId || !img || !ctx.stampMaterial.current || !ctx.stampScene.current || !ctx.orthoCamera.current) return;

    const lRTData = ctx.layerRTs.current.get(selId);
    if (!lRTData) return;

    // Create or update the stamp texture.
    if (!ctx.stampTexture.current) {
      ctx.stampTexture.current = new THREE.CanvasTexture(img);
      ctx.stampTexture.current.colorSpace = THREE.SRGBColorSpace;
    } else {
      ctx.stampTexture.current.image = img;
      ctx.stampTexture.current.needsUpdate = true;
    }

    // Forward rotation matrix for this layer (maps layer-local → world); uses effective
    // transform so that parent rotation/translation offsets are correctly inherited.
    const layer = ctx.layersRef.current.find((l) => l.id === selId);
    const rotFwd = layer
      ? new THREE.Matrix3().setFromMatrix4(buildEffectiveLayerTransform(layer, ctx.layersRef.current))
      : new THREE.Matrix3();

    const cam = ctx.cameraRef.current!;
    const baseCenter = hitPoint.clone().normalize();
    const centers = getDuplicationCenters(baseCenter, ctx.dupModeRef.current);

    const isProjMode = ctx.stampProjectionModeRef.current === 'project';
    const isInverseMode = ctx.stampProjectionModeRef.current === 'inverse';
    const isProjLikeMode = isProjMode || isInverseMode;
    let viewProj = new THREE.Matrix4();
    let centerNDC = new THREE.Vector2();
    let halfSizeNDC = new THREE.Vector2();
    if (isProjLikeMode) {
      viewProj = new THREE.Matrix4().multiplyMatrices(
        cam.projectionMatrix, cam.matrixWorldInverse
      );
      if (isInverseMode) {
        if (!projectBackHitToNdc(hitPoint, cam.position, viewProj, centerNDC)) {
          const ndc = hitPoint.clone().project(cam);
          centerNDC.set(ndc.x, ndc.y);
        }
      } else {
        const ndc = hitPoint.clone().project(cam);
        centerNDC.set(ndc.x, ndc.y);
      }
      const viewAspect = (ctx.glRef.current?.domElement.clientWidth ?? 1) /
                         (ctx.glRef.current?.domElement.clientHeight ?? 1);
      const stampAspectLocal = img.width / img.height;
      const camDist2 = cam.position.length();
      const halfY = (ctx.stampSizeRef.current * camDist2) / camFrustumHalfH(cam, camDist2);
      halfSizeNDC.set(halfY * stampAspectLocal / viewAspect, halfY);
    }

    const baseScale = ctx.stampSizeRef.current * cam.position.length();
    const stampAspect = img.width / img.height;

    // Compute the base tangent frame at the original hit point; each copy
    // rotates it by the dup quaternion.
    const origCenter = hitPoint.clone().normalize();
    let origRight: THREE.Vector3;
    let origUp: THREE.Vector3;
    if (useAppStore.getState().uvViewportMain) {
      // UV panel is primary: align with equirectangular grid so stamps appear
      // upright relative to the UV map.
      ({ sRight: origRight, sUp: origUp } = uvAlignedTangentFrame(origCenter));
    } else {
      const camRight0 = new THREE.Vector3().setFromMatrixColumn(cam.matrixWorld as THREE.Matrix4, 0).normalize();
      const camUp0    = new THREE.Vector3().setFromMatrixColumn(cam.matrixWorld as THREE.Matrix4, 1).normalize();
      origRight = camRight0.clone().addScaledVector(origCenter, -camRight0.dot(origCenter)).normalize();
      origUp    = camUp0.clone().addScaledVector(origCenter, -camUp0.dot(origCenter)).normalize();
    }

    // Determine stamp color — same logic as the draw tool:
    // default = first gradient stop / solid brushColor; Ctrl = last gradient stop.
    let stampColor: string = Array.isArray(ctx.brushColorRef.current)
      ? (ctx.brushColorRef.current as any)[0].color
      : ctx.brushColorRef.current;
    if (ctx.ctrlHeldRef.current) {
      const pv = ctx.pickerValueRef.current;
      if (pv && typeof pv.getColors === 'function') {
        const stops: Array<{ color: any; percent: number }> = pv.getColors();
        if (stops && stops.length > 0) {
          const last = stops[stops.length - 1];
          if (last.color && typeof last.color.toHexString === 'function') {
            const hex8 = last.color.toHexString();
            stampColor = hex8.length >= 9 ? hex8.slice(0, 7) : hex8;
          }
        }
      }
    }

    for (const { center, quaternion } of centers) {
      const srcRT = lRTData.active === 'a' ? lRTData.a : lRTData.b;
      const dstRT = lRTData.active === 'a' ? lRTData.b : lRTData.a;

      const sRight = origRight.clone().applyQuaternion(quaternion);
      const sUp    = origUp.clone().applyQuaternion(quaternion);

      // For projection mode, rotate the viewProj matrix and camera position by
      // the dup quaternion so each copy projects from the equivalent rotated
      // camera.
      let copyViewProj = viewProj;
      let copyCamPos = cam.position;
      if (isProjLikeMode) {
        const rotMatInv = new THREE.Matrix4().makeRotationFromQuaternion(quaternion.clone().invert());
        const viewMatRotated = cam.matrixWorldInverse.clone().multiply(rotMatInv);
        copyViewProj = new THREE.Matrix4().multiplyMatrices(cam.projectionMatrix, viewMatRotated);
        copyCamPos = cam.position.clone().applyQuaternion(quaternion);
      }

      ctx.stampMaterial.current.uniforms.uBase.value         = srcRT.texture;
      ctx.stampMaterial.current.uniforms.uStamp.value        = ctx.stampTexture.current;
      ctx.stampMaterial.current.uniforms.uStampCenter.value  = center;
      ctx.stampMaterial.current.uniforms.uStampRight.value   = sRight;
      ctx.stampMaterial.current.uniforms.uStampUp.value      = sUp;
      ctx.stampMaterial.current.uniforms.uStampCenterUV.value = worldPointToUV(center);
      ctx.stampMaterial.current.uniforms.uStampScale.value   = isProjLikeMode
        ? new THREE.Vector2(baseScale, baseScale / stampAspect)
        : new THREE.Vector2(wrapStampDiam(cam.position.length(), ctx.stampSizeRef.current) * stampAspect, wrapStampDiam(cam.position.length(), ctx.stampSizeRef.current));
      ctx.stampMaterial.current.uniforms.uStampOpacity.value = ctx.brushOpacityRef.current;
      ctx.stampMaterial.current.uniforms.uStampColor.value.setStyle(ctx.stampIsPresetRef.current ? stampColor : '#ffffff');
      ctx.stampMaterial.current.uniforms.uProjMode.value     = isProjMode ? 1 : isInverseMode ? 2 : 0;
      ctx.stampMaterial.current.uniforms.uCamViewProj.value  = copyViewProj;
      ctx.stampMaterial.current.uniforms.uStampCenterNDC.value   = centerNDC;
      ctx.stampMaterial.current.uniforms.uStampHalfSizeNDC.value = halfSizeNDC;
      ctx.stampMaterial.current.uniforms.uCamPos.value       = copyCamPos;
      ctx.stampMaterial.current.uniforms.uStampRotation.value = ctx.stampRotationRef.current * Math.PI / 180;
      ctx.stampMaterial.current.uniforms.uStampAspect.value = stampAspect;
      ctx.stampMaterial.current.uniforms.uRotFwd.value       = rotFwd;

      gl.setRenderTarget(dstRT);
      gl.render(ctx.stampScene.current, ctx.orthoCamera.current);
      gl.setRenderTarget(null);

      lRTData.active = lRTData.active === 'a' ? 'b' : 'a';
    }

    ctx.isDirtyRef.current = true;
    ctx.autosaveNotifyDirtyRef.current();
    if (selId) useAppStore.getState().updateLayer(selId, { lastDupMode: ctx.dupModeRef.current });
    if (selId) ctx.markLayersDirtyRef.current(selId);
    ctx.flattenLayersRef.current();
    ctx.scheduleBumpRef.current(() => {
      if (selId) ctx.generateLayerThumbnailRef.current(selId);
    });
  };

  return {
    buildTextCanvas,
    setStampUniforms,
    renderStampPreview,
    clearStampPreview,
    renderTextPreview,
    applyTextStamp,
    applyStamp,
  };
}
